/**
 * 「ブランドとして意識したい文脈」の登録・編集・削除。
 *
 * これはどのデータ源からも導けないもので、生成も推定もしない。人が書いた
 * ものだけが入るので、画面にも「ここに書いたものだけが入ります」と断る。
 *
 * ペルソナ本体の版（`expectedVersion`）は使わない。文脈は本体と別の口で、
 * 版を持たないためである。
 */

import { useState } from "react";
import { useMutation } from "urql";
import {
  Button,
  Card,
  DeleteBrandContextDocument,
  UpsertBrandContextDocument,
  type PersonaFieldsFragment,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import { type PersonaUserError } from "./PersonaForm";

export function BrandContextsCard({
  persona,
  onChanged,
}: {
  persona: PersonaFieldsFragment;
  onChanged: () => void;
}) {
  const [upsertState, upsertBrandContext] = useMutation(
    UpsertBrandContextDocument,
  );
  const [deleteState, deleteBrandContext] = useMutation(
    DeleteBrandContextDocument,
  );
  const [userErrors, setUserErrors] = useState<PersonaUserError[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [body, setBody] = useState("");

  const busy = upsertState.fetching || deleteState.fetching;

  const reset = (): void => {
    setEditingId(null);
    setLabel("");
    setBody("");
  };

  const handle = (
    promise: Promise<{
      error?: unknown;
      data?: { [key: string]: { userErrors: readonly PersonaUserError[] } };
    }>,
    key: string,
    after: () => void,
  ): void => {
    void promise.then((result) => {
      if (result.error !== undefined) {
        setUserErrors([
          {
            field: null,
            code: "INTERNAL_ERROR",
            message: `保存できませんでした: ${String(result.error)}`,
          },
        ]);
        return;
      }
      const payload = result.data?.[key];
      if (payload === undefined) {
        return;
      }
      if (payload.userErrors.length > 0) {
        setUserErrors([...payload.userErrors]);
        return;
      }
      setUserErrors([]);
      after();
      onChanged();
    });
  };

  return (
    <Card
      title="ブランドとして意識したい文脈"
      extra={<HelpIcon slug="personas/brand-contexts" />}
      note="ここに書いたものだけが入ります。データからは導けないので、推定して埋めることはしません。"
    >
      {userErrors.length === 0 ? null : (
        <div className="vd-notice vd-notice--error" role="alert">
          {userErrors.map((error) => (
            <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
              {error.message}
            </p>
          ))}
        </div>
      )}

      {persona.brandContexts.length === 0 ? (
        <p className="vd-card-note" style={{ marginTop: 0 }}>
          まだ登録がありません。
        </p>
      ) : (
        <ul className="vd-stack" style={{ listStyle: "none", padding: 0 }}>
          {persona.brandContexts.map((context) => (
            <li key={context.id} className="vd-field">
              <strong>{context.label}</strong>
              <p style={{ margin: 0 }}>{context.body}</p>
              <div className="vd-row">
                <Button
                  disabled={busy}
                  aria-label={`「${context.label}」を編集する`}
                  onClick={() => {
                    setEditingId(context.id);
                    setLabel(context.label);
                    setBody(context.body);
                  }}
                >
                  編集する
                </Button>
                <Button
                  disabled={busy}
                  aria-label={`「${context.label}」を削除する`}
                  onClick={() =>
                    handle(
                      deleteBrandContext({
                        input: {
                          brandContextId: context.id,
                          personaId: persona.id,
                        },
                      }),
                      "deleteBrandContext",
                      reset,
                    )
                  }
                >
                  削除する
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="vd-field" style={{ marginTop: 12 }}>
        <label htmlFor="brand-context-label">
          {editingId === null ? "文脈を足す" : "文脈を編集する"}
        </label>
        <span className="vd-field-hint">
          見出しと本文。例: 「縫製は国内の自社工場で」
        </span>
        <input
          id="brand-context-label"
          className="vd-input"
          placeholder="見出し"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
        <textarea
          className="vd-textarea"
          aria-label="文脈の本文"
          placeholder="本文"
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        <div className="vd-row">
          <Button
            variant="primary"
            disabled={busy || label.trim() === "" || body.trim() === ""}
            onClick={() =>
              handle(
                upsertBrandContext({
                  input: {
                    brandContextId: editingId,
                    personaId: persona.id,
                    label: label.trim(),
                    body: body.trim(),
                  },
                }),
                "upsertBrandContext",
                reset,
              )
            }
          >
            {editingId === null ? "文脈を足す" : "文脈を保存する"}
          </Button>
          {editingId === null ? null : (
            <Button disabled={busy} onClick={reset}>
              やめる
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
